import React, { useContext } from "react";
import { Redirect, Switch } from "react-router-dom";
import { PrivateRoute } from "../../components";
import AccountingAccountsScreen from "./AccountingAccountsScreen";
import AccountingFailureScreen from "./AccountingFailureScreen";
import AccountingIntegrationsScreen from "./AccountingIntegrationsScreen";
import AccountingSubScreen from "./AccountingSubScreen";
import AccountingSuccessScreen from "./AccountingSuccessScreen";
import AccountingClassesScreen from "./AccountingClassesScreen";
import AccountingVendorsScreen from "./AccountingVendorsScreen";
import AccountingContext from "../../contexts/accounting";
import ControlhubContext from "../../contexts/controlhub";
import { FREE_PLAN_NAME } from "../../config";
import {
  BillingScreen,
  BillingConfirmationScreen,
  StaticImageScreen,
} from "../../screens";

const AccountingScreenSwitch = ({ basename, service, setLoadingState }) => {
  const { accounting } = useContext(AccountingContext);
  const { currentCompanyPlan } = useContext(ControlhubContext);

  if (!accounting) {
    return null;
  }

  const systemsParam = `:system(${Object.keys(accounting.systems).join("|")})`;

  const handleDefaultRedirect = () => {
    if (!currentCompanyPlan) {
      return null;
    }

    let redirect = null;

    switch (currentCompanyPlan) {
      case FREE_PLAN_NAME:
        redirect = <Redirect to={`${basename}/billing`} />;
        break;
      default:
        redirect = <Redirect to={`${basename}/integrations`} />;
    }

    return redirect;
  };

  return (
    <Switch>
      <PrivateRoute path={`${basename}/billing/success`}>
        <BillingConfirmationScreen
          service={service}
          setLoadingState={setLoadingState}
        />
      </PrivateRoute>

      <PrivateRoute path={`${basename}/billing`}>
        <BillingScreen service={service} setLoadingState={setLoadingState} />
      </PrivateRoute>

      <PrivateRoute path={`${basename}/approvals`}>
        <StaticImageScreen imageName="approval-matrix" />
      </PrivateRoute>

      <PrivateRoute exact path={`${basename}`}>
        {handleDefaultRedirect}
      </PrivateRoute>
      <PrivateRoute path={`${basename}/integrations`}>
        <AccountingSubScreen
          description="Connect ControlHub to your Accounting System to sync your data"
          title="Accounting System Sync"
        >
          <AccountingIntegrationsScreen
            service={service}
            setLoadingState={setLoadingState}
          />
        </AccountingSubScreen>
      </PrivateRoute>
      <PrivateRoute path={`${basename}/${systemsParam}/success`}>
        <AccountingSubScreen
          description="You've connected ControlHub with your Accounting System"
          title="Success!"
        >
          <AccountingSuccessScreen />
        </AccountingSubScreen>
      </PrivateRoute>
      <PrivateRoute path={`${basename}/${systemsParam}/failure`}>
        <AccountingSubScreen
          description="It looks like something went wrong while trying to connect ControlHub to your Accounting System"
          title="Oops!"
        >
          <AccountingFailureScreen />
        </AccountingSubScreen>
      </PrivateRoute>
      <PrivateRoute path={`${basename}/${systemsParam}/account`}>
        <AccountingSubScreen
          description="Select the expense accounts that will be active in ControlHub"
          title="Accounts"
        >
          <AccountingAccountsScreen />
        </AccountingSubScreen>
      </PrivateRoute>
      <PrivateRoute path={`${basename}/${systemsParam}/classes`}>
        <AccountingSubScreen
          description="Select the classes, locations and categories that will be active in ControlHub"
          title="Classes, Locations and Categories"
        >
          <AccountingClassesScreen />
        </AccountingSubScreen>
      </PrivateRoute>
      <PrivateRoute path={`${basename}/vendors`}>
        <AccountingSubScreen description="" title="Vendors">
          <AccountingVendorsScreen />
        </AccountingSubScreen>
      </PrivateRoute>
      {/* {default route: in case no match is found} */}
      <PrivateRoute path={`${basename}`}>
        <Redirect to={`${basename}`} />
      </PrivateRoute>
    </Switch>
  );
};

export default AccountingScreenSwitch;
